import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import { API, useAdminAuth } from '../../App';

const AdminLoginPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const { adminLogin } = useAdminAuth();
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/auth/login`, credentials);
      adminLogin(response.data.token, response.data.admin);
      if (onLogin) onLogin(response.data.token, response.data.admin);
      toast.success('Admin login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Admin login failed', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 bg-accent" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full mx-auto px-6"
      >
        <div className="bg-white border border-border p-8">
          <div className="flex items-center justify-center mb-6">
            <img src="/ambika-logo.jpg" alt="Ambika Fashion" className="h-20 w-auto" />
          </div>
          <h1 className="text-3xl font-heading font-bold text-center mb-2">Admin Login</h1>
          <p className="text-center text-muted-foreground mb-8">Access the admin dashboard</p>

          <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                required
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors"
                placeholder="admin@example.com"
                data-testid="admin-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 pr-12 outline-none transition-colors"
                  placeholder="Enter password"
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="admin-toggle-password"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-3 font-medium transition-all duration-300 disabled:opacity-50"
              data-testid="admin-login-submit"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
